import { getPathBounds } from '../logic/GroupLogic';

/**
 * グループのz-indexをDOM操作で管理するユーティリティ
 */
export class GroupZIndexManager {
  private static instance: GroupZIndexManager;
  private groupBoundsCache = new Map<string, { left: number; top: number; right: number; bottom: number }>();
  
  private constructor() {}
  
  static getInstance(): GroupZIndexManager {
    if (!GroupZIndexManager.instance) {
      GroupZIndexManager.instance = new GroupZIndexManager();
    }
    return GroupZIndexManager.instance;
  }
  
  /**
   * グループの境界を取得（SVGパスから計算）
   */
  private getGroupBounds(groupId: string): { left: number; top: number; right: number; bottom: number } | null {
    // キャッシュから取得
    if (this.groupBoundsCache.has(groupId)) {
      return this.groupBoundsCache.get(groupId)!;
    }
    
    // DOMからSVGパスを取得
    const groupElement = document.querySelector(`[data-group-id="${groupId}"] .group-path`) as SVGPathElement;
    if (!groupElement) return null;
    
    const path = groupElement.getAttribute('d');
    if (!path) return null;
    
    // getPathBoundsを使用して境界を計算
    const bounds = getPathBounds(path);
    
    if (bounds) {
      this.groupBoundsCache.set(groupId, bounds);
    }
    return bounds;
  }
  
  /**
   * 境界キャッシュをクリア
   */
  clearCache(): void {
    this.groupBoundsCache.clear();
  }
  
  /**
   * 特定のグループの境界キャッシュをクリア
   */
  clearGroupCache(groupId: string): void {
    this.groupBoundsCache.delete(groupId);
  }
  

  /**
   * App.tsxの既存ロジックを使用してグループのz-indexを計算
   */
  calculateZIndex(groupId: string, allGroupIds: string[]): number {
    const bounds = this.getGroupBounds(groupId);
    if (!bounds) return 100001; // デフォルト値
    
    // App.tsxの既存ロジック：右下端の位置に基づいてz-indexを計算
    let zIndex = 100001;
    const Z_INDEX_STEP = 10;
    
    for (let i = 0; i < allGroupIds.length; i++) {
      const otherGroupId = allGroupIds[i];
      if (otherGroupId === groupId) continue;
      
      const otherBounds = this.getGroupBounds(otherGroupId);
      if (!otherBounds) continue;
      
      // 右下端のY座標を比較（大きいほど下）
      const bottomDiff = bounds.bottom - otherBounds.bottom;
      if (Math.abs(bottomDiff) > 1) {
        if (bottomDiff > 0) {
          zIndex += Z_INDEX_STEP; // このグループの方が下にある
        }
      } else {
        // Y座標が近い場合はX座標で比較（大きいほど右）
        const rightDiff = bounds.right - otherBounds.right;
        if (rightDiff > 0) {
          zIndex += Z_INDEX_STEP; // このグループの方が右にある
        }
      }
    }
    
    return zIndex;
  }

  /**
   * App.tsxの既存ロジックを使用して全グループのz-indexを再計算
   */
  reorderGroupZIndices(groupIds: string[]): Map<string, number> {
    // グループを右下端の位置でソート
    const sortedGroups = [...groupIds].sort((a, b) => {
      const boundsA = this.getGroupBounds(a);
      const boundsB = this.getGroupBounds(b);
      
      if (!boundsA || !boundsB) return 0;
      
      // 右下端のY座標を比較（大きいほど下）
      const bottomDiff = boundsB.bottom - boundsA.bottom;
      if (Math.abs(bottomDiff) > 1) return bottomDiff;
      
      // Y座標が近い場合はX座標で比較（大きいほど右）
      return boundsB.right - boundsA.right;
    });

    // Z-indexを割り当て（右下が左上にあるほど高く）
    const BASE_Z_INDEX = 100001;
    const Z_INDEX_STEP = 10;
    const zIndexMap = new Map<string, number>();
    
    sortedGroups.forEach((groupId, index) => {
      const zIndex = BASE_Z_INDEX + (index * Z_INDEX_STEP);
      zIndexMap.set(groupId, zIndex);
    });
    
    return zIndexMap;
  }
  
  /**
   * 全グループのz-indexを更新（App.tsxの既存ロジックを使用）
   */
  updateAllGroupZIndices(groupIds: string[]): void {

    
    // App.tsxの既存ロジックを使用してz-indexマップを生成
    const zIndexMap = this.reorderGroupZIndices(groupIds);
    
    // ドラッグ中グループのz-indexオフセット
    const DRAGGING_Z_INDEX_OFFSET = 100000;
    
    // 各グループにz-indexを適用
    zIndexMap.forEach((zIndex, groupId) => {
      // ドラッグ中のグループかどうかをチェック
      const groupWrapperElement = document.querySelector(`[data-group-id="${groupId}"]`) as HTMLElement;
      if (groupWrapperElement?.classList.contains('dragging-flag')) {
        // ドラッグ中のグループは相対的なz-indexを維持しながらオフセットを追加
        this.applyZIndexToGroup(groupId, zIndex + DRAGGING_Z_INDEX_OFFSET);
      } else {
        this.applyZIndexToGroup(groupId, zIndex);
      }
    });
  }
  
  /**
   * 特定のグループにz-indexを適用
   */
  private applyZIndexToGroup(groupId: string, zIndex: number): void {
    // group-wrapperにz-indexを設定（子要素は自動的に継承）
    const groupWrapperElement = document.querySelector(`[data-group-id="${groupId}"]`) as HTMLElement;
    if (groupWrapperElement) {
      groupWrapperElement.style.zIndex = zIndex.toString();
    }
  }
  
  /**
   * グループのz-indexをリセット（デフォルト値に戻す）
   */
  resetGroupZIndex(groupId: string): void {
    // group-wrapperのz-indexをリセット
    const groupWrapperElement = document.querySelector(`[data-group-id="${groupId}"]`) as HTMLElement;
    if (groupWrapperElement) {
      groupWrapperElement.style.zIndex = '';
    }
    
    this.clearGroupCache(groupId);
  }
}
